import { Button, MagellanPopup } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { useFormikContext } from 'formik';
import { ChangeEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PiBroom, PiIdentificationCard, PiScan } from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  ILabelForm,
  ILabelResidenceCertificateMutation,
} from 'services/labels/types';
import { useStateStorage } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import './PopupLabelResidanceCertificateForm.scss';

interface IPopupLabelResidanceCertificateFormProps {
  trigger?: JSX.Element;
  onAdd?: (
    certificate: ILabelResidenceCertificateMutation,
    actions: PopupActions
  ) => void;
}

const PopupLabelResidanceCertificateForm = ({
  trigger,
  onAdd,
}: IPopupLabelResidanceCertificateFormProps) => {
  const { t } = useTranslation();

  const popupRef = useRef<PopupActions>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { setFieldValue, values } = useFormikContext<ILabelForm>();

  const { countries } = useStateStorage();

  const countrySelectOptions =
    countries?.map(country => ({
      label: t(`countries.${country.codeAlfa2}`),
      value: country.codeAlfa2,
    })) ?? [];

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files) {
      setFieldValue(
        'residenceCertificate.ResidenceCertificateFile',
        event.currentTarget.files[0]
      );
      event.currentTarget.value = '';
    }
  };

  const onAddFileClick = () => inputRef.current?.click();

  const fileName =
    values.residenceCertificate?.ResidenceCertificateFile?.name.split('.')[0];
  const fileExtension =
    values.residenceCertificate?.ResidenceCertificateFile?.name.split('.')[1];

  const disabledClearValues =
    !values.residenceCertificate?.ResidenceCertificateFile &&
    !values.residenceCertificate?.ResidenceCertificateCountryCodeAlfa2 &&
    !values.residenceCertificate?.ResidenceCertificateExpirationDate;

  const clearPopupValues = () =>
    setFieldValue('residenceCertificate', undefined);

  const disabledOnAdd =
    !values.residenceCertificate?.ResidenceCertificateFile ||
    !values.residenceCertificate?.ResidenceCertificateCountryCodeAlfa2 ||
    !values.residenceCertificate?.ResidenceCertificateExpirationDate;

  const onAddClick = () =>
    onAdd &&
    popupRef.current &&
    values.residenceCertificate &&
    onAdd(values.residenceCertificate, popupRef.current);

  return (
    <Popup ref={popupRef} modal nested trigger={trigger}>
      <MagellanPopup>
        <MagellanPopup.Header className='justify-center w-80 mb-5'>
          <PiIdentificationCard /> {t('label.certificates.popup.header')}
        </MagellanPopup.Header>
        <FormInput.Select
          name='residenceCertificate.ResidenceCertificateCountryCodeAlfa2'
          label={t('input.label.country')}
          options={countrySelectOptions}
          displayMode={false}
          autoFocus
        />
        <FormInput.Text
          type='date'
          label={t('input.label.expirationDate')}
          name='residenceCertificate.ResidenceCertificateExpirationDate'
          displayMode={false}
        />
        <Button type='button' fullWidth onClick={onAddFileClick}>
          <Button.Icon icon={<PiScan />} />
          <Button.Text text={t('button.add-scan')} />
        </Button>
        <input
          ref={inputRef}
          className='hidden'
          type='file'
          onChange={onInputChange}
        />
        {values.residenceCertificate?.ResidenceCertificateFile && (
          <div className='file-preview-wrapper'>
            <p className='file-preview-title'>
              {t('label.certificates.popup.file')}
            </p>
            <div className='file-preview'>
              <p className='file-preview-name' title={fileName}>
                {fileName}
              </p>
              <p className='file-preview-extension' title={`.${fileExtension}`}>
                .{fileExtension}
              </p>
            </div>
          </div>
        )}

        <MagellanPopup.BottomButtons>
          <Button
            isClear
            onlyIcon
            onClick={clearPopupValues}
            disabled={disabledClearValues}>
            <Button.Icon icon={<PiBroom />} />
          </Button>
          <Button
            variant={BUTTON_VARIANT.submit}
            disabled={disabledOnAdd}
            onClick={onAddClick}>
            <Button.Text text={t('button.add')} />
            <Button.Icon icon={<PiIdentificationCard />} />
          </Button>
        </MagellanPopup.BottomButtons>
      </MagellanPopup>
    </Popup>
  );
};

export default PopupLabelResidanceCertificateForm;
